import type { TFunction } from 'i18next';
import type { TemplateLanguage, TemplateLanguageView, TranslationItem } from '../../../api/hms';
import { MAIN_LANGUAGE } from '../hmsLanguages';

export const OTHER_LANGUAGE = '__other';

/** Checked here for instant feedback. The API validates again. */
const LANGUAGE_CODE = /^[a-z]{2,3}$/;

export type NewLanguage = { selected: string; name: string; code: string };

export type NewLanguageError = 'selectLanguage' | 'nameAndCode' | 'alreadyAdded';

export const newLanguageCode = (input: NewLanguage) =>
  input.selected === OTHER_LANGUAGE ? input.code.trim().toLowerCase() : input.selected;

export const newLanguageError = (input: NewLanguage, used: TemplateLanguage[]): NewLanguageError | null => {
  const code = newLanguageCode(input);
  if (!input.selected) return 'selectLanguage';
  if (input.selected === OTHER_LANGUAGE && (!input.name.trim() || !LANGUAGE_CODE.test(code))) return 'nameAndCode';
  if (code === MAIN_LANGUAGE || used.some((language) => language.code === code)) return 'alreadyAdded';
  return null;
};

export const toNewLanguage = (input: NewLanguage): TemplateLanguage => ({
  code: newLanguageCode(input),
  name: input.selected === OTHER_LANGUAGE ? input.name.trim() : null,
});

/** Languages from the API list that are not added yet */
export const addableLanguages = (selectable: string[], used: TemplateLanguage[]) =>
  selectable.filter((code) => !used.some((language) => language.code === code));

export const progressPercent = (language: Pick<TemplateLanguageView, 'translated' | 'total'>) =>
  language.total ? Math.round((language.translated / language.total) * 100) : 100;

/** Texts AI can suggest: those with neither an own nor a Trustpulse translation */
export const textsToSuggest = (items: TranslationItem[]) =>
  items.filter((item) => !item.text.trim() && item.standard === null);

export type TranslationGroup = { factorName: string | null; kind: 'factor' | 'riskLevels' | 'consequenceLevels'; items: TranslationItem[] };

const groupKind = (item: TranslationItem): TranslationGroup['kind'] =>
  item.factorName ? 'factor' : item.kind === 'riskLevel' ? 'riskLevels' : 'consequenceLevels';

/** One group per factor, then the level texts, in template order */
export const groupTranslationItems = (items: TranslationItem[]): TranslationGroup[] =>
  items.reduce<TranslationGroup[]>((groups, item) => {
    const last = groups[groups.length - 1];
    const kind = groupKind(item);
    return last && last.kind === kind && last.factorName === item.factorName
      ? [...groups.slice(0, -1), { ...last, items: [...last.items, item] }]
      : [...groups, { factorName: item.factorName, kind, items: [item] }];
  }, []);

/** The languages each question is not translated to, by question key */
export const missingLanguagesByQuestion = (languages: TemplateLanguageView[]) =>
  languages
    .flatMap((language) =>
      language.items
        .filter((item) => item.kind === 'question' && !item.translated)
        .map((item) => ({ key: item.key, language })),
    )
    .reduce(
      (missing, { key, language }) => missing.set(key, [...(missing.get(key) ?? []), language]),
      new Map<string, TemplateLanguage[]>(),
    );

export const questionItemKey = (factorKey: string, questionKey: string) => `question:${factorKey}:${questionKey}`;

export const untranslatedCounts = (languages: TemplateLanguageView[]) =>
  languages
    .map((language) => ({ language, missing: language.total - language.translated }))
    .filter(({ missing }) => missing > 0);

/** Label of a text: «Spørsmål 2», «Konsekvens 4» and so on */
export const translationKindLabel = (item: TranslationItem, t: TFunction) =>
  t(`hrRiskFactors.translate.kinds.${item.kind}`, { position: item.position });
